import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Upload, CheckCircle, Loader2, ChevronDown } from 'lucide-react';

const JOB_SECTORS = [
  'Healthcare',
  'IT & Technology',
  'Construction',
  'Finance & Accounting',
  'Hospitality & Tourism',
  'Manufacturing',
  'Retail & Sales',
  'Transportation & Logistics',
  'Education',
  'Engineering',
  'Administrative',
  'Customer Service',
  'Other'
];

export const CTASection = () => {
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    jobSector: '',
  });
  const [resume, setResume] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setResume(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsSubmitting(true);

    try {
      const submitData = new FormData();
      submitData.append('first_name', formData.firstName);
      submitData.append('last_name', formData.lastName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      submitData.append('job_sector', formData.jobSector);
      submitData.append('source', 'resume_submission');
      if (resume) {
        submitData.append('resume', resume);
      }

      const response = await fetch(`${API_URL}/api/resume-submissions`, {
        method: 'POST',
        body: submitData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to submit resume');
      }

      setSubmitSuccess(true);
      setFormData({ firstName: '', lastName: '', email: '', phone: '', jobSector: '' });
      setResume(null);
    } catch (err) {
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitSuccess) {
    return (
      <section className="py-20 md:py-28 px-4 md:px-6 bg-white" data-testid="cta-section">
        <div className="max-w-5xl mx-auto">
          <div className="rounded-3xl p-12 md:p-16 lg:p-20 shadow-xl" style={{ backgroundColor: '#def0ff' }}>
            <div className="text-center">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" />
              <h2 className="text-3xl md:text-4xl font-extrabold text-zinc-900 mb-4">
                Thank You!
              </h2>
              <p className="text-base md:text-lg text-zinc-600 mb-8">
                Your resume has been submitted successfully. Our team will review it and get back to you soon.
              </p>
              <Button
                onClick={() => setSubmitSuccess(false)}
                className="px-8 py-4 bg-brand-blue text-white font-bold rounded-xl hover:bg-brand-blue/90 transition-all"
                data-testid="submit-another-btn"
              >
                Submit Another Resume
              </Button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 md:py-28 px-4 md:px-6 bg-white" data-testid="cta-section">
      <div className="max-w-5xl mx-auto">
        <div className="rounded-3xl p-8 md:p-12 lg:p-16 shadow-xl" style={{ backgroundColor: '#def0ff' }}>
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-zinc-900 mb-4">
              Ready to Take the Next Step?
            </h2>
            <p className="text-base md:text-lg text-zinc-600 max-w-2xl mx-auto">
              Share your resume with us and let our team help you find your perfect opportunity.
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="max-w-2xl mx-auto space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Input
                  type="text"
                  name="firstName"
                  placeholder="First Name *"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border-2 border-transparent focus:border-brand-blue bg-white"
                  data-testid="resume-first-name"
                />
              </div>
              <div>
                <Input
                  type="text"
                  name="lastName"
                  placeholder="Last Name *"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border-2 border-transparent focus:border-brand-blue bg-white"
                  data-testid="resume-last-name"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Input
                  type="email"
                  name="email"
                  placeholder="Email Address *"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 rounded-xl border-2 border-transparent focus:border-brand-blue bg-white"
                  data-testid="resume-email"
                />
              </div>
              <div>
                <Input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 rounded-xl border-2 border-transparent focus:border-brand-blue bg-white"
                  data-testid="resume-phone"
                />
              </div>
            </div>

            {/* Job Sector Dropdown */}
            <div className="relative">
              <select
                name="jobSector"
                value={formData.jobSector}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-3 rounded-xl border-2 border-transparent focus:border-brand-blue bg-white appearance-none cursor-pointer text-zinc-600"
                data-testid="resume-job-sector"
              >
                <option value="" disabled>Select Job Sector *</option>
                {JOB_SECTORS.map((sector) => (
                  <option key={sector} value={sector}>{sector}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400 pointer-events-none" />
            </div>
            
            {/* Resume Upload */}
            <div className="relative">
              <label
                htmlFor="resume-upload"
                className="flex items-center justify-center gap-3 w-full px-4 py-4 bg-white rounded-xl border-2 border-dashed border-zinc-300 hover:border-brand-blue cursor-pointer transition-colors"
              >
                <Upload className="w-5 h-5 text-zinc-500" />
                <span className="text-zinc-600">
                  {resume ? resume.name : 'Upload Resume (PDF, DOC, DOCX) *'}
                </span>
              </label>
              <input
                id="resume-upload"
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleFileChange}
                required
                className="sr-only"
                data-testid="resume-file-input"
              />
            </div>

            {error && (
              <div className="text-red-600 text-sm text-center bg-red-50 py-2 px-4 rounded-lg">
                {error}
              </div>
            )}

            <div className="text-center pt-4">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="px-10 py-5 bg-brand-blue text-white font-bold rounded-xl hover:bg-brand-blue/90 transition-all text-lg shadow-lg hover:shadow-xl hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="submit-resume-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  'Submit Resume'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};
